/**
 * AuditoriaPage — lista de auditorias (F10).
 *
 * O domínio inteiro está na SPA: envio dos documentos (/auditoria/nova),
 * acompanhamento (/tasks/auditoria/<id>), seleção do que será auditado
 * (/auditoria/<id>/selecao) e o relatório (/auditoria/<id>).
 */

import {
  faCheckCircle,
  faExclamationTriangle,
  faEye,
  faFileUpload,
  faHandPointer,
  faSearchDollar,
  faSpinner,
  faTrashAlt,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router'
import { excluirAuditoria, listarAuditorias } from '../../api/auditoria'
import { useToast } from '../../components/Toast'

export function AuditoriaPage() {
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()

  const { data, isLoading } = useQuery({
    queryKey: ['auditorias'],
    queryFn: listarAuditorias,
    // auditorias em andamento mudam de estado sozinhas
    refetchInterval: 10_000,
  })

  const excluir = useMutation({
    mutationFn: (id: number) => excluirAuditoria(id),
    onSuccess: () => {
      void clienteQuery.invalidateQueries({ queryKey: ['auditorias'] })
      notificar('success', 'Auditoria excluída.')
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  const auditorias = data?.auditorias ?? []

  return (
    <div className="mx-auto max-w-5xl">
      <header className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <div className="bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500 p-6 text-white">
          <h1 className="flex items-center text-2xl font-bold">
            <FontAwesomeIcon icon={faSearchDollar} className="mr-3" />
            Auditoria de Documentos
          </h1>
          <p className="mt-1 text-rose-100">
            Análise de editais, contratos e processos com apontamentos
            fundamentados na Lei 14.133/2021 e na jurisprudência.
          </p>
        </div>
        <div className="p-6">
          <Link
            to="/auditoria/nova"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-rose-500 to-amber-500 px-6 py-3 font-semibold text-white shadow-lg transition-all hover:from-rose-600 hover:to-amber-600"
          >
            <FontAwesomeIcon icon={faFileUpload} />
            Nova auditoria
          </Link>
        </div>
      </header>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <h2 className="mb-4 text-lg font-semibold text-slate-800 dark:text-slate-100">
          Auditorias
        </h2>
        {isLoading ? (
          <p className="text-sm text-slate-500">
            <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
            Carregando…
          </p>
        ) : auditorias.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500 dark:border-slate-600">
            Nenhuma auditoria ainda.
          </p>
        ) : (
          <ul className="space-y-3">
            {auditorias.map((a) => {
              const status = (a.status || '').toUpperCase()
              const concluida = status === 'CONCLUIDA'
              const aguardando = status === 'AGUARDANDO_SELECAO'
              const falhou = status === 'ERRO' || !!a.erro
              return (
                <li
                  key={a.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 p-4 dark:border-slate-700"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-slate-800 dark:text-slate-100">
                      {a.arquivo || `Auditoria #${a.id}`}
                    </p>
                    <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                      {a.criado_em} ·{' '}
                      <span
                        className={
                          falhou
                            ? 'text-red-600 dark:text-red-400'
                            : concluida
                              ? 'text-emerald-600 dark:text-emerald-400'
                              : 'text-amber-600 dark:text-amber-400'
                        }
                      >
                        <FontAwesomeIcon
                          icon={
                            falhou
                              ? faExclamationTriangle
                              : concluida
                                ? faCheckCircle
                                : aguardando
                                  ? faHandPointer
                                  : faSpinner
                          }
                          spin={!concluida && !falhou && !aguardando}
                          className="mr-1"
                        />
                        {falhou
                          ? a.erro || 'Erro'
                          : concluida
                            ? 'Concluída'
                            : aguardando
                              ? 'Aguardando sua seleção'
                              : `${a.mensagem || 'Processando'} (${a.percentual}%)`}
                      </span>
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Link
                      to={
                        concluida
                          ? `/auditoria/${a.id}`
                          : aguardando
                            ? `/auditoria/${a.id}/selecao`
                            : `/tasks/auditoria/${a.id}`
                      }
                      className="inline-flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200"
                    >
                      <FontAwesomeIcon
                        icon={
                          concluida
                            ? faEye
                            : aguardando
                              ? faHandPointer
                              : faSpinner
                        }
                      />
                      {concluida
                        ? 'Ver relatório'
                        : aguardando
                          ? 'Selecionar documentos'
                          : 'Acompanhar'}
                    </Link>
                    <button
                      type="button"
                      onClick={() => excluir.mutate(a.id)}
                      aria-label={`Excluir auditoria ${a.arquivo}`}
                      className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-red-600 transition-colors hover:bg-red-50 dark:border-slate-600 dark:hover:bg-red-900/20"
                    >
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </button>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </section>
    </div>
  )
}
